import { IZone, IZoneCleaningHistory } from 'models';
import { apiVersion } from '../apis.const';
import { transformResponse } from '../apis.util';
import { baseApi } from '../base.api';
import { ZoneServiceLogFormValues } from '../../../forms/zones/zone-service-log/zone-service-log-form.hook';

/**
 * Create APIs
 */
export const zonesApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (build) => ({
    fetchZones: build.query<FetchZonesRes, FetchZonesReq>({
      query: ({ orgSlug }) => ({
        url: `/${orgSlug}/${apiVersion}/zone?rating=true&last_serviced=true`,
      }),
      transformResponse,
      providesTags: ['Zones'],
    }),

    fetchZone: build.query<FetchZoneRes, FetchZoneReq>({
      query: ({ orgSlug, id }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/${id}`,
      }),
      transformResponse,
      providesTags: (result, error, { id }) => [{ type: 'Zone', id }],
    }),

    fetchZoneWithoutAuth: build.query<FetchZoneWithoutAuthRes, FetchZoneWithoutAuthReq>({
      query: ({ id }) => ({
        url: `/${apiVersion}/zone/${id}`,
      }),
      transformResponse,
    }),

    createZone: build.mutation<CreateZoneRes, CreateZoneReq>({
      query: ({ orgSlug, zone }) => ({
        url: `/${orgSlug}/${apiVersion}/zone`,
        method: 'POST',
        body: zone,
      }),
      transformResponse,
      invalidatesTags: ['Zones'],
    }),

    updateZone: build.mutation<UpdateZoneRes, UpdateZoneReq>({
      query: ({ orgSlug, id, zone }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/${id}`,
        method: 'POST',
        body: zone,
      }),
      transformResponse,
      invalidatesTags: (result, error, { id }) => [{ type: 'Zone', id }, 'Zones'],
    }),

    deleteZone: build.mutation<DeleteZoneRes, DeleteZoneReq>({
      query: ({ orgSlug, id }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/${id}`,
        method: 'DELETE',
      }),
      transformResponse,
      invalidatesTags: ['Zones'],
    }),

    inactivateZone: build.mutation<InactivateZoneRes, InactivateZoneReq>({
      query: ({ orgSlug, id }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/${id}/inactive`,
        method: 'PUT',
      }),
      transformResponse,
      invalidatesTags: ['Zones'],
    }),

    activateZone: build.mutation<ActivateZoneRes, ActivateZoneReq>({
      query: ({ orgSlug, id }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/${id}/activate`,
        method: 'PUT',
      }),
      transformResponse,
      invalidatesTags: ['Zones'],
    }),

    /**
     * Zone service items
     */
    fetchZoneServiceItems: build.query<FetchZoneServiceItemsRes, FetchZoneServiceItemsReq>({
      query: ({ orgSlug, zoneId }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/${zoneId}/service-item`,
      }),
      transformResponse,
      providesTags: (result, error, { zoneId }) => [{ type: 'ZoneServiceItems', zoneId }],
    }),

    createZoneServiceItem: build.mutation<CreateZoneServiceItemRes, CreateZoneServiceItemReq>({
      query: ({ orgSlug, zoneId, serviceItem }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/${zoneId}/service-item`,
        method: 'POST',
        body: serviceItem,
      }),
      transformResponse,
      invalidatesTags: (result, error, { zoneId }) => [{ type: 'ZoneServiceItems', zoneId }, 'Zones'],
    }),

    updateZoneServiceItem: build.mutation<UpdateZoneServiceItemRes, UpdateZoneServiceItemReq>({
      query: ({ orgSlug, zoneId, id, serviceItem }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/${zoneId}/service-item/${id}`,
        method: 'PUT',
        body: serviceItem,
      }),
      transformResponse,
      invalidatesTags: (result, error, { zoneId }) => [{ type: 'ZoneServiceItems', zoneId }, 'Zones'],
    }),

    deleteZoneServiceItem: build.mutation<DeleteZoneServiceItemReq, DeleteZoneServiceItemRes>({
      query: ({ orgSlug, zoneId, id }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/${zoneId}/service-item/${id}`,
        method: 'DELETE',
      }),
      transformResponse,
      invalidatesTags: (result, error, { zoneId }) => [{ type: 'ZoneServiceItems', zoneId }, 'Zones'],
    }),

    /**
     * Zone cleaning history
     */
    fetchZoneCleaningHistory: build.query<FetchZoneCleaningHistoryRes, FetchZoneCleaningHistoryReq>({
      query: ({ zoneId, date }) => ({
        url: `/${apiVersion}/zone/${zoneId}/cleaning-history?date=${date}`,
      }),
      transformResponse,
    }),

    /**
     * Public zone endpoint
     */
    publicFetchZone: build.query<PublicFetchZoneRes, PublicFetchZoneReq>({
      query: ({ code }) => ({
        url: `/${apiVersion}/zone/code/${code}`,
      }),
      transformResponse,
      providesTags: (result, error, { code }) => [{ type: 'Zone', id: code }],
    }),

    /**
     * Public zone log endpoint
     */
    publicFetchZoneLog: build.query<PublicFetchZoneLogRes, PublicFetchZoneLogReq>({
      query: ({ zoneId, limit }) => ({
        url: `/${apiVersion}/zone/${zoneId}/log?limit=${limit || 30}`,
      }),
      transformResponse,
    }),

    /**
     * zone feedback
     */
    zoneFeedback: build.mutation<ZoneFeedbackRes, ZoneFeedbackReq>({
      query: (request) => ({
        url: `/v1/zone/${request.zoneId}/contact-form`,
        method: 'POST',
        body: request,
      }),
      transformResponse,
    }),

    /**
     * zone rating
     */
    zoneRating: build.mutation<ZoneRatingRes, ZoneRatingReq>({
      query: (request) => ({
        url: `/v1/zone/${request.zoneId}/feedback`,
        method: 'POST',
        body: request,
      }),
      transformResponse,
    }),

    /**
     * zone service log
     */
    zoneServiceLog: build.mutation<ZoneServiceLogRes, ZoneServiceLogReq>({
      query: (request) => ({
        url: `/v1/zone/${request.zoneId}/log`,
        method: 'POST',
        body: request.body,
      }),
      transformResponse,
    }),

    zoneServiceLogImages: build.mutation<ZoneServiceLogImagesRes, ZoneServiceLogImagesReq>({
      query: (request) => ({
        url: `/v1/zone/log/${request.logId}/files`,
        method: 'POST',
        body: request.body,
      }),
      transformResponse,
    }),

    /**
     * zone service log
     */
    subscribeZone: build.mutation<SubscribeZoneRes, SubscribeZoneReq>({
      query: ({ zoneId, phone }) => ({
        url: `/v1/zone/${zoneId}/subscribe`,
        method: 'POST',
        body: { phone },
      }),
      transformResponse,
    }),

    /**
     * Zone Logo
     */
    deleteZoneLogo: build.mutation<DeleteZoneLogoRes, DeleteZoneLogoReq>({
      query: ({ orgSlug, id }) => ({
        url: `/${orgSlug}/${apiVersion}/zone/${id}/logo`,
        method: 'DELETE',
      }),
      transformResponse,
    }),
  }),
});

/**
 * Hooks
 */
export const {
  useFetchZonesQuery,
  useFetchZoneQuery,
  useFetchZoneWithoutAuthQuery,
  useCreateZoneMutation,
  useUpdateZoneMutation,
  useDeleteZoneMutation,
  useInactivateZoneMutation,
  useActivateZoneMutation,
  useFetchZoneServiceItemsQuery,
  useCreateZoneServiceItemMutation,
  useUpdateZoneServiceItemMutation,
  useDeleteZoneServiceItemMutation,
  useFetchZoneCleaningHistoryQuery,
  usePublicFetchZoneQuery,
  usePublicFetchZoneLogQuery,
  useZoneFeedbackMutation,
  useZoneRatingMutation,
  useZoneServiceLogMutation,
  useZoneServiceLogImagesMutation,
  useSubscribeZoneMutation,
  useDeleteZoneLogoMutation,
} = zonesApi;

/**
 * Types
 */
type OrgSlug = {
  orgSlug: string;
};
type OrgSlugAndId = OrgSlug & {
  id: number;
};
type ServiceItem = {
  name: string;
};

type FetchZonesRes = any;
type FetchZonesReq = OrgSlug;
type FetchZoneRes = any;
type PublicFetchZoneRes = any;
type PublicFetchZoneReq = { code: string };
type PublicFetchZoneLogRes = any;
type PublicFetchZoneLogReq = { zoneId: number; limit?: number };
type ZoneFeedbackReq = { zoneId: number; name: string; phone: string; email: string; message: string };
type ZoneFeedbackRes = any;
type ZoneRatingReq = { zoneId: number; rating: number };
type ZoneRatingRes = any;
type ZoneServiceLogReq = {
  zoneId: number;
  body: ZoneServiceLogFormValues;
};
type SubscribeZoneRes = any;
type SubscribeZoneReq = {
  zoneId: number;
  phone: string;
};
type ZoneServiceLogRes = any;
type ZoneServiceLogImagesRes = any;
type ZoneServiceLogImagesReq = {
  logId: number;
  body: FormData;
};
type FetchZoneReq = OrgSlugAndId;
type FetchZoneWithoutAuthRes = any;
type FetchZoneWithoutAuthReq = {
  id: number;
};
type CreateZoneRes = any;
type CreateZoneReq = OrgSlug & {
  zone: IZone;
};
type UpdateZoneRes = any;
type UpdateZoneReq = OrgSlugAndId & {
  zone: IZone;
};
type DeleteZoneRes = any;
type DeleteZoneReq = OrgSlugAndId;
type InactivateZoneRes = any;
type InactivateZoneReq = OrgSlugAndId;
type ActivateZoneRes = any;
type ActivateZoneReq = OrgSlugAndId;
type FetchZoneServiceItemsRes = any;
type FetchZoneServiceItemsReq = OrgSlug & {
  zoneId: number;
};
type CreateZoneServiceItemRes = any;
type CreateZoneServiceItemReq = OrgSlug & {
  zoneId: number;
  serviceItem: ServiceItem;
};
type UpdateZoneServiceItemRes = any;
type UpdateZoneServiceItemReq = OrgSlugAndId & {
  zoneId: number;
  serviceItem: ServiceItem;
};
type DeleteZoneServiceItemReq = any;
type DeleteZoneServiceItemRes = OrgSlugAndId & {
  zoneId: number;
};
type FetchZoneCleaningHistoryRes = IZoneCleaningHistory;
type FetchZoneCleaningHistoryReq = {
  zoneId: number;
  date: string;
};
type DeleteZoneLogoRes = any;
type DeleteZoneLogoReq = OrgSlugAndId;
